import { HttpClient } from '@adapters/HttpClient';
import { CRMModel } from '../models/CRM';
import { Filters } from '../models/Filters';

export interface CRMPage {
  orders: CRMModel[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

export interface ExportCRMPayload {
  search?: string;
  team_ids?: string;
  crm_status_id?: number;
  shop_ids?: string;
  tag_id?: number;
  payment_method_ids?: string;
  product_ids?: string;
  date_from?: string;
  date_to?: string;
  shipping_country_codes?: string;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface ExportCRMResponse {
  url_file: string;
}

export interface ExportCRMBody {
  columns?: string[];
}

export class CRMApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getCRMPaginated(filters: Filters = {}): Promise<CRMPage> {
    const {
      search,
      team_ids,
      crm_status_id,
      shop_ids,
      tag_id,
      payment_method_ids,
      product_ids,
      date_from,
      date_to,
      shipping_country_codes,
      sort_by = 'created_at',
      sort_order = 'desc',
      page = 1,
      limit = 25,
    } = filters;

    const params: Record<string, any> = {
      page,
      per_page: limit,
    };

    if (search) {
      params.search = search;
    }
    if (team_ids) {
      params.team_ids = team_ids;
    }
    if (crm_status_id !== undefined) {
      if (crm_status_id === null) {
        params.without_crm_status = 1;
      } else {
        params.crm_status_id = crm_status_id;
      }
    }
    if (shop_ids) {
      params.shop_ids = shop_ids;
    }
    if (tag_id) {
      params.tag_id = tag_id;
    }
    if (shipping_country_codes) {
      // Backend espera `shipping_country_codes[]` como array en querystring.
      params['shipping_country_codes[]'] = shipping_country_codes;
    }
    if (payment_method_ids) {
      params.payment_method_ids = payment_method_ids;
    }
    if (product_ids) {
      params.product_ids = product_ids;
    }
    if (date_from) {
      params.date_from = date_from;
    }
    if (date_to) {
      params.date_to = date_to;
    }
    if (sort_by) {
      params.sort_by = sort_by;
    }
    if (sort_order) {
      params.sort_order = sort_order;
    }

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/crm/orders', params, {}, showGlobalLoading);

    // Asegura que orders sea siempre un array, extrayendo de response.data
    const orders = Array.isArray(response?.data) ? response.data : [];

    // Normalizar crm_status: convertir objeto a array si es necesario
    const normalizedOrders = orders.map((order: any) => {
      if (order.crm_status && !Array.isArray(order.crm_status)) {
        // Si crm_status es un objeto, convertirlo a array
        return {
          ...order,
          crm_status: [order.crm_status],
        };
      }
      return order;
    });

    const rawMeta = response?.meta ?? {};
    // API puede devolver current_page/last_page (snake_case) o currentPage/lastPage (camelCase)
    const currentPage = rawMeta.currentPage ?? rawMeta.current_page ?? 1;
    const lastPage = rawMeta.lastPage ?? rawMeta.last_page ?? 1;
    const total = rawMeta.total ?? 0;

    return {
      orders: normalizedOrders,
      nextPage: currentPage < lastPage ? currentPage + 1 : undefined,
      hasMore: currentPage < lastPage,
      total,
    };
  }

  async getAll(): Promise<CRMModel[]> {
    const response = await this.getCRMPaginated();
    return response.orders;
  }

  async exportCRMExcel(filters: Filters = {}): Promise<string> {
    const params: Record<string, any> = {};

    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.team_ids) {
      params.team_ids = filters.team_ids;
    }
    if (filters.crm_status_id !== undefined) {
      params.crm_status_id = filters.crm_status_id === null ? '' : filters.crm_status_id;
    }
    if (filters.shop_ids) {
      params.shop_ids = filters.shop_ids;
    }
    if (filters.tag_id) {
      params.tag_id = filters.tag_id;
    }
    if (filters.shipping_country_codes) {
      params.shipping_country_codes = filters.shipping_country_codes;
    }
    if (filters.product_ids) {
      params.product_ids = filters.product_ids;
    }
    if (filters.date_from) {
      params.date_from = filters.date_from;
    }
    if (filters.date_to) {
      params.date_to = filters.date_to;
    }
    if (filters.sort_by) {
      params.sort_by = filters.sort_by;
    }
    if (filters.sort_order) {
      params.sort_order = filters.sort_order;
    }

    // Enviar parámetros en el body (no en querystring)
    const response = await this.httpClient.post<any>('/crm/orders/export', params);
    return response.url_file || response.url || response.data?.url_file || response.data?.url;
  }

  async confirmOrder(id: number): Promise<void> {
    await this.httpClient.put(`/crm/orders/${id}/confirm`, {});
  }

  async cancelOrder(id: number): Promise<void> {
    await this.httpClient.put(`/crm/orders/${id}/cancel`, {});
  }

  async uncancelOrder(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/uncancel`, {});
  }

  async confirmOrdersBulk(ids: number[]): Promise<void> {
    await this.httpClient.post('/crm/orders/bulk/confirm', { order_ids: ids });
  }

  async cancelOrdersBulk(ids: number[]): Promise<void> {
    await this.httpClient.post('/crm/orders/bulk/cancel', { order_ids: ids });
  }

  async uncancelOrdersBulk(ids: number[]): Promise<void> {
    await this.httpClient.post('/crm/orders/bulk/uncancel', { order_ids: ids });
  }

  async getCRMOrdersCountByStatus(filters: Filters = {}): Promise<{ counts: Record<string, number>; total: number }> {
    const params: Record<string, any> = {};

    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.team_ids) {
      params.team_ids = filters.team_ids;
    }
    if (filters.shop_ids) {
      params.shop_ids = filters.shop_ids;
    }
    if (filters.tag_id) {
      params.tag_id = filters.tag_id;
    }
    if (filters.shipping_country_codes) {
      params['shipping_country_codes[]'] = filters.shipping_country_codes;
    }
    if (filters.payment_method_ids) {
      params.payment_method_ids = filters.payment_method_ids;
    }
    if (filters.product_ids) {
      params.product_ids = filters.product_ids;
    }
    if (filters.date_from) {
      params.date_from = filters.date_from;
    }
    if (filters.date_to) {
      params.date_to = filters.date_to;
    }

    const response = await this.httpClient.get<Record<string, number>>('/crm/orders/count-by-status', params);
    if (!response) return { counts: {}, total: 0 };

    const { without_status, total, ...statusCounts } = response as any;
    const counts: Record<string, number> = { ...statusCounts };
    if (without_status !== undefined) {
      counts['null'] = without_status;
    }
    return { counts, total: total ?? 0 };
  }

  async exportCRMOrders(payload: ExportCRMPayload, visibleColumns?: string[]): Promise<ExportCRMResponse> {
    // Construir query string con los filtros
    const params: Record<string, any> = {};

    if (payload.search) {
      params.search = payload.search;
    }
    if (payload.team_ids) {
      params.team_ids = payload.team_ids;
    }
    if (payload.crm_status_id) {
      params.crm_status_id = payload.crm_status_id;
    }
    if (payload.shop_ids) {
      params.shop_ids = payload.shop_ids;
    }
    if (payload.tag_id) {
      params.tag_id = payload.tag_id;
    }
    if (payload.shipping_country_codes) {
      params['shipping_country_codes[]'] = payload.shipping_country_codes;
    }
    if (payload.payment_method_ids) {
      params.payment_method_ids = payload.payment_method_ids;
    }
    if (payload.product_ids) {
      params.product_ids = payload.product_ids;
    }
    if (payload.date_from) {
      params.date_from = payload.date_from;
    }
    if (payload.date_to) {
      params.date_to = payload.date_to;
    }

    const sortBy = payload.sort_by || 'created_at';
    const sortDirection = payload.sort_direction || 'desc';
    params.sort_by = sortBy;
    params.sort_direction = sortDirection;

    // Construir query string
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    const url = `/crm/orders/export${queryString ? `?${queryString}` : ''}`;

    // Construir body con columnas si se proporcionan
    const body: ExportCRMBody = {};
    if (visibleColumns && visibleColumns.length > 0) {
      body.columns = visibleColumns;
    }

    return await this.httpClient.post(url, body);
  }
}
